export type SignGroup = 'vehicle' | 'command' | 'area';

// Organisation colours of the tactical signs that exist for more than one carrier.
export const ORGANISATIONS = ['hilfsorganisation', 'feuerwehr'] as const;
export type Organisation = (typeof ORGANISATIONS)[number];

export type SignDef = {
  id: string;
  label: string; // shown to the instructor
  group: SignGroup;
  isUnit: boolean; // true: placing it creates/binds a Unit
  hidden?: boolean; // kept for stored data, not offered for new placements
  organisations?: readonly Organisation[]; // selectable carriers, the first is the default
};

const RESCUE_FIRST: readonly Organisation[] = ['hilfsorganisation', 'feuerwehr'];
const FIRE_FIRST: readonly Organisation[] = ['feuerwehr', 'hilfsorganisation'];

export const SIGNS: SignDef[] = [
  { id: 'rtw', label: 'RTW', group: 'vehicle', isUnit: true, organisations: RESCUE_FIRST },
  { id: 'ktw', label: 'KTW', group: 'vehicle', isUnit: true, organisations: RESCUE_FIRST },
  { id: 'nktw', label: 'NKTW', group: 'vehicle', isUnit: true, organisations: RESCUE_FIRST },
  { id: 'nef', label: 'NEF', group: 'vehicle', isUnit: true, organisations: RESCUE_FIRST },
  { id: 'naw', label: 'NAW', group: 'vehicle', isUnit: true, organisations: RESCUE_FIRST },
  { id: 'itw', label: 'ITW', group: 'vehicle', isUnit: true, organisations: RESCUE_FIRST },
  { id: 'grtw', label: 'GRTW', group: 'vehicle', isUnit: true, organisations: RESCUE_FIRST },
  { id: 'gwsan', label: 'GW-SAN', group: 'vehicle', isUnit: true, organisations: RESCUE_FIRST },
  { id: 'elw', label: 'ELW', group: 'vehicle', isUnit: true, organisations: FIRE_FIRST },
  { id: 'lf', label: 'LF', group: 'vehicle', isUnit: true },
  { id: 'rth', label: 'RTH', group: 'vehicle', isUnit: true, organisations: RESCUE_FIRST },
  { id: 'orgl', label: 'OrgL', group: 'command', isUnit: true },
  { id: 'lna', label: 'LNA', group: 'command', isUnit: true },
  { id: 'eal', label: 'EAL', group: 'command', isUnit: true },
  { id: 'uhs', label: 'UHS', group: 'command', isUnit: true },
  { id: 'commandPost', label: 'Einsatzleitung', group: 'command', isUnit: false },
  { id: 'staging', label: 'Bereitstellungsraum', group: 'area', isUnit: false },
  { id: 'casualtyCollection', label: 'Patientenablage', group: 'area', isUnit: false },
  { id: 'treatment', label: 'Behandlungsplatz', group: 'area', isUnit: false },
  { id: 'assembly', label: 'Sammelstelle', group: 'area', isUnit: false },
  { id: 'helipad', label: 'Hubschrauberlandeplatz', group: 'area', isUnit: false },
  { id: 'vehicleHolding', label: 'Rettungsmittelhalteplatz', group: 'area', isUnit: false },
  { id: 'loadingZone', label: 'Ladezone', group: 'area', isUnit: false },
  { id: 'section', label: 'Einsatzabschnitt', group: 'area', isUnit: false },
  // Not a term of the current standard; stored scenarios and runs still reference it.
  {
    id: 'casualtyStation',
    label: 'Verletztensammelstelle',
    group: 'area',
    isUnit: false,
    hidden: true,
  },
];

export const SIGN_IDS = SIGNS.map((s) => s.id) as [string, ...string[]];
export type SignType = (typeof SIGNS)[number]['id'];

// Records read back from IndexedDB are never re-validated, so a run stored before a sign
// left the catalogue must not crash the app mid-exam. Every caller only wants a label, so a
// fallback SignDef (labelled with the unknown id) is safer than throwing.
function fallbackSign(id: string): SignDef {
  return { id, label: id, group: 'area', isUnit: false };
}

export function signById(id: string): SignDef {
  return SIGNS.find((s) => s.id === id) ?? fallbackSign(id);
}

// The carrier to store for a sign: the chosen one if the sign offers it, else none, which
// means the sign's default. Keeps stale carriers out of files when a type changes.
export function offeredOrganisation(
  id: string,
  chosen: Organisation | undefined,
): Organisation | undefined {
  return chosen && signById(id).organisations?.includes(chosen) ? chosen : undefined;
}

// The carrier a sign is drawn for: the requested one if the sign knows it, else its default.
export function signOrganisation(id: string, requested?: Organisation): Organisation | undefined {
  const options = signById(id).organisations;
  if (!options) return undefined;
  return requested && options.includes(requested) ? requested : options[0];
}
